import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@core/environments/environment';
import { Observable, finalize, firstValueFrom } from 'rxjs';
import { Pagination } from '@core/models/pagination.model';
import { LoadingService } from './loading.service';
import { URL_KEYS } from '@core/constants/url-keys.constants';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  BASE_URL = environment.ADMIN_BASE_URL + '/v1';

  http = inject(HttpClient);
  private loadingService = inject(LoadingService);

  constructor() { }

  getBankList(page?: Pagination, search?: string): Observable<BankListResponse> {
    const uniqueStateUrl = URL_KEYS.COMMON.GET_BANK_LIST;
    this.loadingService.setLoading(uniqueStateUrl, true);
    
    let params = new HttpParams();
    
    if (page) {
      params = params
        .set('take', page.size.toString())
        .set('skip', page.skip.toString());
    }

    if (search) {
      params = params.set('search_text', search);
    }

    return this.http
      .get<BankListResponse>(`${this.BASE_URL}/bank/v2/get-bank-list`, { params })
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  createBank(payload: CreateBankPayload): Observable<any> {
    const uniqueStateUrl = 'create-bank';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<any>(this.BASE_URL + '/bank/create-bank-code', payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  updateBank(payload: UpdateBankPayload): Observable<any> {
    const uniqueStateUrl = 'update-bank';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<any>(this.BASE_URL + '/bank/update-bank-code', payload)
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  async getBankName(bankCode: string): Promise<string | undefined> {
    const uniqueStateUrl = URL_KEYS.COMMON.GET_BANK_NAME;
    this.loadingService.setLoading(uniqueStateUrl, true);
    try {
      // Convert the observable to a promise
      const res = await firstValueFrom(this.getBankList());

      const banks = res.data;

      const bank = banks.find((bank) => bank.bankCode === bankCode);

      return bank ? bank.bankName : undefined;
    } catch (error) {
      console.error('Error fetching bank name:', error);
      return undefined;
    } finally {
      this.loadingService.setLoading(uniqueStateUrl, false);
    }
  }

  // TODO: update method to match the updated API
  // validateAccountNumber(account_number: string, bank_code: string) {
  //   return this.http.post(this.BASE_URL + '/bank/v2/get-bank-list', {
  //     account_number,
  //     bank_code,
  //   });
  // }

  validateBankAccountNo(
    accountNumber: string,
    bankCode: string
    
  ): Observable<AccountValidationResponse> {
    const uniqueStateUrl = 'validate-bank-details';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<AccountValidationResponse>(
        this.BASE_URL + '/bank/validate-bank-details',
        {
          account_number: accountNumber,
          bank_code: bankCode,
        }
      )
      .pipe(
       
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }

  getAllCountriesAndCities(): Observable<unknown> {
    return this.http.get('https://countriesnow.space/api/v0.1/countries');
  }

  getAllNigerianStates(): Observable<unknown> {
    return this.http.get(
      'https://nigeria-states-towns-lga.onrender.com/api/states'
    );
  }
  getAccountDetails(account_number: string): Observable<any> {
    return this.http.get(
      'https://nigeria-states-towns-lga.onrender.com/api/states'
    );
  }

  getJointAccountName(account: any[]) {
    return account.reduce((p, c, i) => {
      if (i > 0) {
        return (
          p +
          ` & ${c.personal_details.first_name} ${c.personal_details.last_name.toLocaleUpperCase()}`
        );
      }
      return `${c.personal_details.first_name} ${c.personal_details.last_name.toLocaleUpperCase()}`;
    }, '');
  }

  getAccountName(account: any): string {

    if (account.account_type === 'corporate') {
      return account.corporate_kyc?.name || '-'
    }
    if (account.account_type === 'joint') {
      return this.getJointAccountName(account.joint_kyc) || '-'
    }
    if (account.account_type === 'individual') {
      let name = ""
      if (account.user_profile.last_name) {
        name = account.user_profile.last_name.toLocaleUpperCase() + " "
      }
      if (account.user_profile.first_name) {
        name += account.user_profile.first_name
      }
      if (account.user_profile.middle_name) {
        name = name + " " + account.user_profile.middle_name
      }
      return name
    }
    return '-'
  }

  validateUserDetails(data: UserDetailsValidator): Observable<UserDetailsValidatorResponse> {
    return this.http.post<UserDetailsValidatorResponse>(this.BASE_URL + '/admin/validate-user-details', data);
  }

  getProfileDetails(bvn: string): Observable<ProfileDetailsResponse> {
    const uniqueStateUrl = 'get-profile-details';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .get<ProfileDetailsResponse>(
        this.BASE_URL + `/admin/get-identity-detail-with-bvn?bvn=${bvn}`
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }


  refreshTransactionStatus(transactionReference:string) {
    const uniqueStateUrl = 'refresh-transaction-status';
    this.loadingService.setLoading(uniqueStateUrl, true);
    return this.http
      .post<any>(
        this.BASE_URL + `/bank/verify-transaction-by-reference?transaction_reference=${transactionReference}`, {}
      )
      .pipe(
        finalize(() => this.loadingService.setLoading(uniqueStateUrl, false))
      );
  }
}



export interface AccountValidationResponse {
  status: string;
  message: string;
  data: AccountValidationResponseData;
}

export interface AccountValidationResponseData {
  bankCode: string;
  accountName: string;
  transactionReference: string;
  bvn: string;
  responseMessage: string;
  accountNumber: string;
  responseCode: string;
}

export interface BankListResponse {
  status: string;
  message: string;
  data_count?: number;
  data: BankListDatum[];
  pagination?: {
    total_count: number;
    page_count: number;
    current_page: number;
    per_page: number;
  };
}

export interface BankListDatum {
  bankCode: string;
  bankName: string;
  providus_code?: string;
  paystack_code?: string;
}

export interface CreateBankPayload {
  bank_name: string;
  providus_code?: string;
  paystack_code?: string;
}

export interface UpdateBankPayload {
  bank_code_id: string;
  bank_name?: string;
  providus_code?: string;
  paystack_code?: string;
}


export interface UserDetailsValidator {
  email?: string;
  phone_number?: string;
  bvn?: string;
}

interface UserDetailsValidatorResponse {
  status: string;
  message: string;
}
interface ProfileDetailsResponse {
  status: string;
  message: string;
  data: BVNProfileDetail;
}

export interface BVNProfileDetail {
  id: string;
  type: string;
  user_id: string;
  identity_number: string;
  first_name: string;
  last_name: string;
  middle_name: string | null;
  full_name: string | null;
  gender: string;
  date_of_birth: string;
  residential_address: string;
  marital_status: string;
  nationality: string;
  state_of_origin: string;
  title: string | null;
  occupation: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  state_of_residence: null;
  phone_number1: null;
  phone_number2: null;
}